import { Injectable, inject } from '@angular/core';
import { Observable, from, map, catchError, throwError } from 'rxjs';
import {
    createProjectWebClient,
    ServiceClientError,
    type ProjectWebClient,
} from '@workgraph/project-service-web-client';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import type { Project, CreateProjectRequest, UpdateProjectRequest } from '../models/project.model';

/** How the caught error was classified — components branch on this, never on message text. */
export type ProjectErrorKind = 'not-found' | 'generic';

/**
 * A renderable, typed error the components can branch on by CODE rather than message text.
 *
 * `.message` is always the user-facing copy (components render `err.message`); `.kind` and
 * `.status` are the reliable discriminators derived from the client's `statusCode`.
 */
export class ProjectError extends Error {
    constructor(
        message: string,
        readonly kind: ProjectErrorKind,
        readonly status?: number,
    ) {
        super(message);
        this.name = 'ProjectError';
    }
}

/**
 * Thin Angular seam between DI and the generated project-service web-client.
 *
 * It owns exactly four concerns and NOTHING else (no per-endpoint URL/HTTP wiring — that
 * lives in the generated SDK): (1) constructing the web-client with the api-gateway host and
 * the Cognito bearer token, (2) bridging the client's promises to RxJS Observables,
 * (3) unwrapping the `{data}` / `{data,count}` response envelopes, and (4) mapping errors to
 * friendly, renderable, TYPED `ProjectError`s.
 *
 * Error mapping note: classification derives PURELY from `statusCode` (reliable), NEVER from
 * message text (unreliable — a bare HTTP reason phrase on some clients, EMPTY over HTTP/2 in
 * prod). Any 404 → not-found; everything else → a per-operation fallback. A meaningful,
 * non-generic `.message` (present when the client reads the `{error}` body) is surfaced as a
 * fidelity bonus but is never depended upon.
 */
@Injectable({ providedIn: 'root' })
export class ProjectGateway {
    private readonly auth = inject(AuthService);

    private readonly client: ProjectWebClient = createProjectWebClient({
        baseUrl: environment.gatewayBaseUrl,
        getToken: () => this.auth.getIdToken(),
    });

    /**
     * List the signed-in user's projects. The SPA sends the end-user id as the `ownerId`
     * query param (mirroring the agent flow), so the call works regardless of the gateway
     * header; server-side, the gateway's `x-requester-id` header remains authoritative.
     */
    listProjects(ownerId: string | undefined): Observable<Project[]> {
        const query = ownerId ? { ownerId } : undefined;
        return from(this.client.listProjects({ query })).pipe(
            map((result) => result.data?.data ?? []),
            catchError((err) =>
                this.fail(err, 'We could not load your projects. Please try again.'),
            ),
        );
    }

    getProject(id: string): Observable<Project> {
        return from(this.client.getProject({ path: { id } })).pipe(
            map((result) => this.requireData(result.data?.data, 'project')),
            catchError((err) =>
                this.fail(err, 'We could not load this project. Please try again.'),
            ),
        );
    }

    createProject(body: CreateProjectRequest): Observable<Project> {
        return from(this.client.createProject({ body })).pipe(
            map((result) => this.requireData(result.data?.data, 'project')),
            catchError((err) =>
                this.fail(err, 'We could not create the project. Please try again.'),
            ),
        );
    }

    updateProject(id: string, body: UpdateProjectRequest): Observable<Project> {
        return from(this.client.updateProject({ path: { id }, body })).pipe(
            map((result) => this.requireData(result.data?.data, 'project')),
            catchError((err) =>
                this.fail(err, 'We could not update this project. Please try again.'),
            ),
        );
    }

    deleteProject(id: string): Observable<void> {
        return from(this.client.deleteProject({ path: { id } })).pipe(
            map(() => void 0),
            catchError((err) =>
                this.fail(err, 'We could not delete this project. Please try again.'),
            ),
        );
    }

    /** Guard against a 2xx with an unexpectedly empty envelope. */
    private requireData<T>(data: T | undefined, what: string): T {
        if (data === undefined || data === null) {
            throw new Error(`The server returned no ${what} data.`);
        }
        return data;
    }

    /** Map a thrown client error to a friendly, renderable, TYPED `ProjectError`. */
    private fail(err: unknown, fallback: string): Observable<never> {
        return throwError(() => this.toProjectError(err, fallback));
    }

    /**
     * Classify the caught error by `statusCode` (reliable) — never by message text (unreliable).
     * - any 404 → not-found.
     * - everything else → the per-operation fallback (or a specific non-generic message if present).
     */
    private toProjectError(err: unknown, fallback: string): ProjectError {
        if (err instanceof ServiceClientError) {
            const status = err.statusCode;
            const specific = this.meaningfulMessage(err.message);

            if (status === 404) {
                return new ProjectError(
                    specific ?? 'This project was not found.',
                    'not-found',
                    404,
                );
            }
            return new ProjectError(specific ?? fallback, 'generic', status);
        }
        if (err instanceof Error && err.message) {
            return new ProjectError(err.message, 'generic');
        }
        return new ProjectError(fallback, 'generic');
    }

    /**
     * Return the error's own message only when it carries something meaningful — i.e. non-empty
     * AND not a bare HTTP reason phrase. Returns `undefined` otherwise so callers fall back.
     */
    private meaningfulMessage(raw: string | undefined | null): string | undefined {
        const message = (raw ?? '').trim();
        if (!message || this.isGenericHttpMessage(message)) {
            return undefined;
        }
        return message;
    }

    private isGenericHttpMessage(message: string): boolean {
        const generic = [
            'bad request',
            'not found',
            'internal server error',
            'forbidden',
            'unauthorized',
        ];
        const normalized = message.toLowerCase();
        return generic.some(
            (g) => normalized === g || normalized === `request failed with status ${g}`,
        );
    }
}
